/**
 * Utility functions for the AdminLogin component
 * These functions provide common formatting and data manipulation capabilities
 */

/**
 * Formats a phone number into a consistent format: 123-456-7890
 *
 * @param {string|number|null} phoneNumber - The phone number to format
 * @returns {string} Formatted phone number or empty string if invalid
 */
export const formatPhoneNumber = (phoneNumber) => {
  // Return early if phoneNumber is null, undefined, or not a string
  if (!phoneNumber) return "";

  // Convert to string if it's not already
  const phoneStr = String(phoneNumber);

  // Remove all non-numeric characters
  const cleaned = phoneStr.replace(/\D/g, "");

  // Check if we have a 10-digit number
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10
    )}`;
  }

  // Check if we have an 11-digit number starting with 1 (US country code)
  if (cleaned.length === 11 && cleaned.charAt(0) === "1") {
    return `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(
      7,
      11
    )}`;
  }

  // If it doesn't match our expected formats, return the input unchanged
  return phoneStr;
};

/**
 * Formats a date string into a human-readable format
 *
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return "";

  const dateObj = date instanceof Date ? date : new Date(date);

  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return "";

  // Format as MM/DD/YYYY
  return `${
    dateObj.getMonth() + 1
  }/${dateObj.getDate()}/${dateObj.getFullYear()}`;
};

/**
 * Formats a date string specifically for event displays
 *
 * @param {string} dateStr - Date string in ISO format (YYYY-MM-DD)
 * @returns {string} Formatted date for event display (e.g., "Mon, Jan 1")
 */
export const formatEventDate = (dateStr) => {
  if (!dateStr) return "";

  // Use T12:00:00 to avoid timezone issues when parsing date-only string
  // This ensures we're parsing in the middle of the day to avoid day shift issues
  const dateWithTime = `${dateStr}T12:00:00`;
  const date = new Date(dateWithTime);

  // Log for debugging timezone issues
  console.log("formatEventDate:", {
    input: dateStr,
    dateWithTime,
    date,
    utcString: date.toUTCString(),
    isoString: date.toISOString(),
    localString: date.toString(),
    timezoneOffset: date.getTimezoneOffset(),
  });

  // Check if the date is valid
  if (isNaN(date.getTime())) return dateStr;

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

/**
 * Formats an array field for display or storage
 *
 * @param {string|Array} field - The array field to format
 * @param {string} action - Either 'parse' (string→array) or 'stringify' (array→string)
 * @returns {string|Array} Formatted field
 */
export const formatArrayField = (field, action = "parse") => {
  if (action === "parse") {
    // Convert string to array
    if (!field) return [];
    if (Array.isArray(field)) return field;

    // Try to parse JSON first
    try {
      const parsed = JSON.parse(field);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // Not JSON, try comma-separated string
    }

    // Split by comma and trim whitespace
    return field
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  } else {
    // Convert array to string
    if (!field) return "";
    if (typeof field === "string") return field;

    // Join array with commas
    return Array.isArray(field) ? field.join(", ") : String(field);
  }
};

/**
 * Enhanced fetch function that handles CORS properly for cross-origin requests
 *
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options including method, headers, body, etc.
 * @returns {Promise<Response>} The fetch response
 */
export const fetchWithCORS = async (url, options = {}) => {
  // Set default headers if not provided
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Add CORS settings
  const corsOptions = {
    ...options,
    headers,
    mode: "cors",
    // Don't include credentials by default as it may cause preflight issues
    // with some APIs that don't explicitly allow credentials
  };

  try {
    const response = await fetch(url, corsOptions);
    return response;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};
